import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Web Speech API binding for the AI composer's mic button, augmented
 * with a parallel Web Audio AnalyserNode so callers can render a real
 * audio-reactive visualisation while the user is talking.
 *
 * Speech recognition path (Chrome / Edge / Safari behind the
 * `webkitSpeechRecognition` prefix; Firefox not at all):
 *   - `interimResults: true` so consumers can render the live
 *     transcript as it streams in, not just the final string.
 *   - Final transcript fires `onResult`. Auto-send is the caller's
 *     decision.
 *
 * Audio level path:
 *   - Separate getUserMedia stream tapped via an AnalyserNode. We
 *     return the analyser so the consumer can drive a waveform
 *     visualiser at 60fps without paying React-render cost per
 *     frame (the visualiser writes to DOM directly via refs).
 *   - The audio context is created on `start` and closed on `stop`
 *     to release the mic indicator cleanly.
 */
export interface VoiceCaptureOpts {
  /** Final transcript — fires once per turn. */
  onResult: (text: string) => void;
  /** Optional callback for interim (in-progress) transcripts. */
  onInterim?: (text: string) => void;
  onError?: (message: string) => void;
  /** BCP-47 language tag. Defaults to the user agent's language. */
  language?: string;
}

interface SpeechRecognitionEventLike {
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean; length: number }>;
  resultIndex: number;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string; message?: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface VoiceCaptureHandle {
  /** True iff the Speech Recognition API is available. */
  supported: boolean;
  listening: boolean;
  /**
   * AnalyserNode tied to the current mic stream — lives only while
   * `listening`. Consumers wire this into a Web Audio visualiser.
   */
  analyser: AnalyserNode | null;
  /** Live in-progress transcript. Reset when listening stops. */
  interim: string;
  start: () => void;
  stop: () => void;
}

export function useVoiceCapture({
  onResult,
  onInterim,
  onError,
  language,
}: VoiceCaptureOpts): VoiceCaptureHandle {
  const [listening, setListening] = useState(false);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [interim, setInterim] = useState('');

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const audioRef = useRef<{
    ctx: AudioContext;
    stream: MediaStream;
  } | null>(null);

  const supported = typeof window !== 'undefined' && getRecognitionCtor() !== null;

  // Latest callbacks live in refs so `start` doesn't have to re-mount
  // its recognition + audio plumbing every render.
  const onResultRef = useRef(onResult);
  const onInterimRef = useRef(onInterim);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  useEffect(() => {
    onInterimRef.current = onInterim;
  }, [onInterim]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const teardownAudio = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    audioRef.current = null;
    for (const track of a.stream.getTracks()) {
      track.stop();
    }
    // `AudioContext.close` returns a promise; we ignore it because the
    // mic-release side effect happens immediately and any close error
    // is benign (already-closed contexts reject).
    void a.ctx.close().catch(() => {});
    setAnalyser(null);
  }, []);

  const setupAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      // Pull AudioContext off the (still-prefixed in Safari) window.
      const Ctor =
        (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) {
        throw new Error('Web Audio API is not available.');
      }
      const ctx = new Ctor();
      const source = ctx.createMediaStreamSource(stream);
      const node = ctx.createAnalyser();
      // 256 → 128 frequency bins → 9-bar visualiser maps cleanly.
      // Smoothing on the analyser side hides sample-rate jitter so the
      // bars don't twitch when the mic picks up silence between words.
      node.fftSize = 256;
      node.smoothingTimeConstant = 0.72;
      source.connect(node);
      audioRef.current = { ctx, stream };
      setAnalyser(node);
    } catch (err) {
      // Permission denied / no device / Safari blocking — non-fatal
      // for the recognition path itself, but the visualiser will just
      // sit flat. We still let recognition proceed.
      onErrorRef.current?.(
        err instanceof Error ? err.message : 'Microphone unavailable.',
      );
    }
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      onErrorRef.current?.('Voice capture is not supported in this browser.');
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    const recog = new Ctor();
    recog.lang = language ?? navigator.language ?? 'en-US';
    recog.continuous = false;
    recog.interimResults = true;

    recog.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? '';
        if (result?.isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      if (interimText) {
        setInterim(interimText);
        onInterimRef.current?.(interimText);
      }
      const trimmed = finalText.trim();
      if (trimmed) {
        onResultRef.current(trimmed);
      }
    };
    recog.onerror = (event) => {
      // `no-speech` and `aborted` are noisy non-events — happens every
      // time the user stops without saying anything. Don't toast them.
      const code = event.error;
      if (code && code !== 'no-speech' && code !== 'aborted') {
        onErrorRef.current?.(event.error || event.message || 'Voice capture failed.');
      }
      setListening(false);
      setInterim('');
      teardownAudio();
    };
    recog.onend = () => {
      setListening(false);
      setInterim('');
      recognitionRef.current = null;
      teardownAudio();
    };
    recognitionRef.current = recog;
    setInterim('');
    void setupAudio();
    try {
      recog.start();
      setListening(true);
    } catch (err) {
      onErrorRef.current?.(
        err instanceof Error ? err.message : 'Could not start voice capture.',
      );
      setListening(false);
      recognitionRef.current = null;
      teardownAudio();
    }
  }, [language, setupAudio, teardownAudio]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    // `onend` will fire and finish the teardown, but in case the
    // browser delays it, tear down audio eagerly so the mic indicator
    // releases the moment the user taps stop.
    teardownAudio();
  }, [teardownAudio]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
      teardownAudio();
    };
  }, [teardownAudio]);

  return { supported, listening, analyser, interim, start, stop };
}
